import React, { useState, useImperativeHandle, forwardRef } from 'react';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import { ThemeStyle } from '../../module/ThemeManager';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

type Props = {
    content: string;
    theme?: ThemeStyle;
    className?: string;
    lineClamp?: number;
    enableReadMore?: boolean;
    onReadMoreClick?: () => void;
    handleExpandInternally?: boolean;
    hideReadMoreText?: boolean;
    textStyle?: React.CSSProperties;
    characterLimit?: number;
    textColor?: string;
};

export type RichTextViewerRef = {
    expand: () => void;
};

const RichTextViewer = forwardRef<RichTextViewerRef, Props>(
    (
        {
            content,
            className = '',
            lineClamp,
            enableReadMore = false,
            onReadMoreClick,
            handleExpandInternally = false,
            hideReadMoreText,
            textStyle,
            characterLimit,
            textColor,
        },
        ref,
    ) => {
        const [showFullText, setShowFullText] = useState(() => !(lineClamp || handleExpandInternally));
        useImperativeHandle(ref, () => ({ expand }));
        const { themeStyle } = useThemeContext();

        const expand = () => {
            if (handleExpandInternally) setShowFullText(true);
            onReadMoreClick?.();
        };

        if (!content || !content.trim()) return null;

        //  Decode escaped HTML before sanitizing
        const decodeHtml = (html: string): string => {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = html;
            return textarea.value;
        };

        const decodedHtml = decodeHtml(content);
        const sanitizedHtml = DOMPurify.sanitize(decodedHtml);

        const isClamped = Boolean(lineClamp) && !showFullText;

        return (
            <div className={`rich-text-wrapper ${className}`}>
                <div
                    className={`ql-editor ${themeStyle === 'dark' ? 'rich-text-dark' : 'rich-text-light'}`}
                    style={{
                        ...(isClamped && lineClamp
                            ? {
                                  display: 'block',
                                  maxHeight: `${lineClamp * 2.7}em`,
                                  overflow: 'hidden',
                              }
                            : {}),
                        ...textStyle,
                    }}
                    dangerouslySetInnerHTML={{
                        __html: `<div style="color: ${
                            textColor ? textColor : theme[themeStyle].tag_white
                        } !important">${
                            characterLimit ? `${sanitizedHtml.slice(0, characterLimit)}` : sanitizedHtml
                        }</div>`,
                    }}
                />
                {enableReadMore && lineClamp && !hideReadMoreText && (
                    <button
                        onClick={() => {
                            if (handleExpandInternally) {
                                setShowFullText(prev => !prev);
                            } else {
                                onReadMoreClick?.();
                            }
                        }}
                        style={{
                            marginTop: '0.5em',
                            cursor: 'pointer',
                            color: '#007BFF',
                            background: 'none',
                            border: 'none',
                        }}>
                        {showFullText ? 'Read Less' : 'Read More'}
                    </button>
                )}
            </div>
        );
    },
);

export { RichTextViewer };
